<template>
  <v-container color="primary" class="pa-4" style="max-width: 1400px;">

    <!-- Error general -->
    <v-layout row wrap v-if="generalError" class="mb-3">
      <v-flex xs12>
        <v-alert type="error" dismissible :value="true" @input="generalError = ''">
          {{ generalError }}
        </v-alert>
      </v-flex>
    </v-layout>

    <!-- Statistics Widgets -->
    <v-layout row wrap class="mb-4">
      <v-flex xs12 sm6 md2 lg2>
        <v-card class="mx-2 my-2 text-xs-center blue lighten-5">
          <v-card-text>
            <div class="display-1 font-weight-bold blue--text text--darken-4">{{ stats.total }}</div>
            <div class="caption blue--text text--darken-2 font-weight-bold">Total Espacios</div>
          </v-card-text>
        </v-card>
      </v-flex>
      <v-flex xs12 sm6 md2 lg2>
        <v-card class="mx-2 my-2 text-xs-center green lighten-5">
          <v-card-text>
            <div class="display-1 font-weight-bold green--text text--darken-4">{{ stats.available }}</div>
            <div class="caption green--text text--darken-2 font-weight-bold">Disponibles</div>
          </v-card-text>
        </v-card>
      </v-flex>
      <v-flex xs12 sm6 md2 lg2>
        <v-card class="mx-2 my-2 text-xs-center red lighten-5">
          <v-card-text>
            <div class="display-1 font-weight-bold red--text text--darken-4">{{ stats.occupied }}</div>
            <div class="caption red--text text--darken-2 font-weight-bold">Ocupados</div>
          </v-card-text>
        </v-card>
      </v-flex>
      <v-flex xs12 sm6 md2 lg2>
        <v-card class="mx-2 my-2 text-xs-center orange lighten-5">
          <v-card-text>
            <div class="display-1 font-weight-bold orange--text text--darken-4">{{ stats.blocked }}</div>
            <div class="caption orange--text text--darken-2 font-weight-bold">Bloqueados</div>
          </v-card-text>
        </v-card>
      </v-flex>
      <v-flex xs12 sm6 md2 lg2>
        <v-card class="mx-2 my-2 text-xs-center grey lighten-3">
          <v-card-text>
            <div class="display-1 font-weight-bold grey--text text--darken-3">{{ stats.maintenance }}</div>
            <div class="caption grey--text text--darken-2 font-weight-bold">Mantenimiento</div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Main Card -->
    <v-card class="rounded-lg elevation-4 overflow-hidden">
      <v-toolbar flat color="white" class="border-bottom" style="height: auto; min-height: 56px;">
        <v-icon color="primary" class="mr-2">local_parking</v-icon>
        <v-toolbar-title class="font-weight-bold primary--text" style="font-size: 1.5rem; white-space: normal; line-height: 1.3;">
          Estado de Estacionamiento en Tiempo Real
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <!-- Refresh interval -->
        <div style="width: 120px; margin-right: 6px;" class="d-flex align-center hidden-xs-only">
          <v-text-field
            v-model.number="refreshMinutes"
            label="Min recarga"
            type="number"
            min="1"
            dense
            outlined
            hide-details
            @change="updateRefreshInterval"
            color="primary"
          ></v-text-field>
        </div>
        <v-btn icon color="primary" @click="fetchAll" :loading="loading" title="Recargar">
          <v-icon>refresh</v-icon>
        </v-btn>
        <v-btn icon color="primary" @click="toggleFullscreen">
          <v-icon>{{ isFullscreen ? 'fullscreen_exit' : 'fullscreen' }}</v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider></v-divider>

      <v-card-text class="pa-4 pa-sm-6">

        <!-- Company + type filters -->
        <div class="d-flex align-center justify-space-between mb-5 flex-wrap" style="gap: 8px;">
          <!-- Company filter -->
          <div class="d-flex align-center flex-wrap" style="gap: 8px;">
            <v-select
              v-model="selectedCompanyFilter"
              :items="companies"
              item-text="name"
              item-value="id"
              label="Filtrar por empresa"
              prepend-icon="business"
              outlined
              dense
              hide-details
              clearable
              style="width: 240px; max-width: 100%;"
            ></v-select>
            <v-text-field
              v-model="searchSpaces"
              prepend-icon="search"
              label="Buscar espacio o empleado..."
              outlined
              dense
              hide-details
              clearable
              style="width: 240px; max-width: 100%;"
            ></v-text-field>
          </div>

          <!-- Type filter -->
          <div class="d-flex align-center flex-wrap" style="gap: 8px;">
            <v-btn-toggle v-model="selectedTypeFilter" mandatory dense>
              <v-btn small value="all" color="white">
                <v-icon small class="mr-1">apps</v-icon>Todas
              </v-btn>
              <v-btn v-for="t in typeMeta" :key="t.type" small :value="t.type" color="white">
                <v-icon small class="mr-1">{{ t.icon }}</v-icon>{{ t.label }}
              </v-btn>
            </v-btn-toggle>
          </div>
        </div>

        <!-- Legend (centrada y responsiva) -->
        <div class="d-flex justify-center flex-wrap mb-5" style="gap: 16px;">
          <div class="d-flex align-center">
            <div style="width:18px;height:18px;border-radius:5px;background:#4CAF50;flex-shrink:0;" class="mr-2"></div>
            <span class="caption font-weight-medium">Disponible</span>
          </div>
          <div class="d-flex align-center">
            <div style="width:18px;height:18px;border-radius:5px;background:#F44336;flex-shrink:0;" class="mr-2"></div>
            <span class="caption font-weight-medium">Ocupado</span>
          </div>
          <div class="d-flex align-center">
            <div style="width:18px;height:18px;border-radius:5px;background:#FF9800;flex-shrink:0;" class="mr-2"></div>
            <span class="caption font-weight-medium">Bloqueado</span>
          </div>
          <div class="d-flex align-center">
            <div style="width:18px;height:18px;border-radius:5px;background:#9E9E9E;flex-shrink:0;" class="mr-2"></div>
            <span class="caption font-weight-medium">Mantenimiento</span>
          </div>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="d-flex justify-center align-center pa-10">
          <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
        </div>

        <!-- Parking grid by type -->
        <div v-else-if="filteredGroups.length === 0" class="text-center pa-10 grey--text">
          <v-icon large color="grey lighten-1">local_parking</v-icon>
          <div class="subheading mt-2">No hay espacios que coincidan con los filtros.</div>
        </div>

        <div v-else>
          <div v-for="group in filteredGroups" :key="group.type" class="mb-6">
            <!-- Section header -->
            <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px;">
              <v-icon color="primary">{{ group.icon }}</v-icon>
              <span class="title font-weight-bold primary--text">{{ group.label }}</span>
              <v-chip small color="blue lighten-5" text-color="blue darken-3">
                {{ group.spaces.length }} espacios
              </v-chip>
            </div>

            <!-- Spaces grid -->
            <v-layout row wrap>
              <v-flex
                v-for="space in group.spaces"
                :key="space.id"
                xs6 sm4 md3 lg2
              >
                <v-card
                  class="ma-1 parking-space-card elevation-2"
                  :class="getSpaceCardClass(space)"
                  @click="openSpaceDetail(space)"
                  style="cursor: pointer; border-radius: 10px; transition: transform 0.15s; min-height: 100px;"
                >
                  <v-card-text class="pa-3 d-flex flex-column align-center justify-center text-center" style="height:100%;">
                    <v-icon :color="getSpaceIconColor(space)" large>
                      {{ group.icon }}
                    </v-icon>
                    <div class="font-weight-bold mt-1" style="font-size: 0.95rem;">{{ space.code }}</div>
                    <div class="caption mt-1" style="opacity: 0.8;">{{ space.type }}</div>
                    <v-chip x-small class="mt-2 font-weight-bold" :color="getSpaceChipColor(space)" dark>
                      {{ getSpaceStatusLabel(space) }}
                    </v-chip>
                    <!-- Company / employee if occupied -->
                    <div v-if="space.company_name" class="caption mt-1 text-truncate" style="max-width:100%; font-weight:600; opacity:0.85;">
                      {{ space.company_name }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-flex>
            </v-layout>

            <v-divider v-if="filteredGroups.indexOf(group) < filteredGroups.length - 1" class="mt-4"></v-divider>
          </div>
        </div>

      </v-card-text>
    </v-card>

    <!-- Space detail dialog -->
    <v-dialog v-model="dialogSpaceDetail" max-width="480px" v-if="selectedSpace">
      <v-card class="rounded-xl elevation-8 overflow-hidden">
        <v-toolbar flat dark :color="getSpaceChipColor(selectedSpace)" class="premium-toolbar">
          <v-icon left>local_parking</v-icon>
          <v-toolbar-title class="font-weight-bold">Espacio {{ selectedSpace.code }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogSpaceDetail = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6 mt-2">
          <v-layout column>
            <!-- Status chip -->
            <v-flex class="mb-4 text-center">
              <v-chip
                :color="getSpaceChipColor(selectedSpace)"
                dark label
                class="px-4 font-weight-bold headline mb-2"
                style="height:auto; padding: 8px 16px; border-radius: 12px;"
              >
                {{ getSpaceStatusLabel(selectedSpace) }}
              </v-chip>
              <div class="grey--text text--darken-1 font-weight-medium">Estado del espacio</div>
            </v-flex>

            <v-divider class="mb-5"></v-divider>

            <v-layout row wrap>
              <!-- Space code -->
              <v-flex xs6 class="mb-4 pr-2">
                <div class="d-flex align-start">
                  <v-icon color="primary" class="mr-3 mt-1">local_parking</v-icon>
                  <div>
                    <div class="caption grey--text font-weight-bold text-uppercase">Espacio</div>
                    <div class="body-2 font-weight-bold">{{ selectedSpace.code }}</div>
                  </div>
                </div>
              </v-flex>

              <!-- Type -->
              <v-flex xs6 class="mb-4 pl-2">
                <div class="d-flex align-start">
                  <v-icon color="primary" class="mr-3 mt-1">commute</v-icon>
                  <div>
                    <div class="caption grey--text font-weight-bold text-uppercase">Tipo</div>
                    <div class="body-2 font-weight-bold">{{ selectedSpace.type }}</div>
                  </div>
                </div>
              </v-flex>

              <!-- Company -->
              <v-flex xs6 class="mb-4 pr-2">
                <div class="d-flex align-start">
                  <v-icon color="primary" class="mr-3 mt-1">business</v-icon>
                  <div>
                    <div class="caption grey--text font-weight-bold text-uppercase">Empresa</div>
                    <div class="body-2 font-weight-bold">{{ selectedSpace.company_name || 'No asignado' }}</div>
                  </div>
                </div>
              </v-flex>

              <!-- Employee -->
              <v-flex xs6 class="mb-4 pl-2">
                <div class="d-flex align-start">
                  <v-icon color="primary" class="mr-3 mt-1">person</v-icon>
                  <div>
                    <div class="caption grey--text font-weight-bold text-uppercase">Empleado</div>
                    <div class="body-2 font-weight-bold">{{ selectedSpace.employee_name || 'Vacío' }}</div>
                  </div>
                </div>
              </v-flex>
            </v-layout>

            <!-- Occupied: show access info -->
            <div v-if="selectedSpace.status === 'ocupado'">
              <v-divider class="mb-4"></v-divider>
              <div class="caption grey--text font-weight-bold text-uppercase mb-3">Información de Acceso</div>

              <v-layout row wrap>
                <v-flex xs12 class="mb-3">
                  <div class="d-flex align-start">
                    <v-icon color="orange" class="mr-3 mt-1">login</v-icon>
                    <div>
                      <div class="caption grey--text font-weight-bold text-uppercase">Hora Entrada</div>
                      <div class="body-2 font-weight-bold orange--text">{{ selectedSpace.check_in_time || 'N/A' }}</div>
                    </div>
                  </div>
                </v-flex>
              </v-layout>
            </div>

            <!-- Available -->
            <v-alert
              v-if="selectedSpace.status === 'disponible'"
              type="success"
              outline
              color="green"
              class="rounded-lg mt-2"
              icon="check_circle"
            >
              Este espacio está disponible.
            </v-alert>

            <!-- Blocked -->
            <v-alert
              v-if="selectedSpace.status === 'bloqueado'"
              type="warning"
              outline
              color="orange"
              class="rounded-lg mt-2"
              icon="block"
            >
              Este espacio está bloqueado{{ selectedSpace.block_reason ? ': ' + selectedSpace.block_reason : '' }}.
            </v-alert>

            <!-- Maintenance -->
            <v-alert
              v-if="selectedSpace.status === 'mantenimiento'"
              type="info"
              outline
              color="grey darken-1"
              class="rounded-lg mt-2"
              icon="build"
            >
              Este espacio está en mantenimiento.
            </v-alert>

          </v-layout>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-btn
            depressed
            color="primary"
            dark
            class="text-none font-weight-bold"
            @click="openChangeStatusDialog"
            style="border-radius: 12px;"
          >
            <v-icon left small>edit</v-icon>
            Cambiar Estado
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn block depressed large color="grey lighten-2" class="text-none font-weight-bold" @click="dialogSpaceDetail = false" style="border-radius: 12px;">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Change Status Dialog -->
    <v-dialog v-model="dialogChangeStatus" max-width="500px" v-if="selectedSpace">
      <v-card class="rounded-xl elevation-8">
        <v-toolbar flat dark color="primary" class="premium-toolbar">
          <v-icon left>edit</v-icon>
          <v-toolbar-title class="font-weight-bold">Cambiar Estado - Espacio {{ selectedSpace.code }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogChangeStatus = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6 mt-4">
          <v-form ref="formChangeStatus">
            <v-layout column>
              <v-flex xs12 class="mb-6">
                <div class="caption grey--text font-weight-bold text-uppercase mb-2">Estado Actual</div>
                <v-chip
                  :color="getSpaceChipColor(selectedSpace)"
                  dark
                  class="px-4 font-weight-bold"
                  style="height: auto; padding: 8px 16px; border-radius: 12px;"
                >
                  {{ getSpaceStatusLabel(selectedSpace) }}
                </v-chip>
              </v-flex>

              <v-divider class="mb-6"></v-divider>

              <v-flex xs12 class="mb-4">
                <v-select
                  v-model="changeStatusForm.status"
                  :items="statusOptions"
                  label="Nuevo Estado"
                  prepend-icon="local_parking"
                  outlined
                  dense
                  :rules="[v => !!v || 'Selecciona un estado']"
                ></v-select>
              </v-flex>

              <!-- Block reason -->
              <v-flex xs12 class="mb-4" v-if="changeStatusForm.status === 'bloqueado'">
                <v-text-field
                  v-model="changeStatusForm.block_reason"
                  label="Motivo del bloqueo"
                  prepend-icon="note"
                  outlined
                  dense
                ></v-text-field>
              </v-flex>

              <!-- Employee selection when marking as ocupado -->
              <v-flex xs12 class="mb-4" v-if="changeStatusForm.status === 'ocupado'">
                <v-alert v-if="!selectedSpace.company" type="warning" outline color="orange" dense class="mb-3">
                  Este espacio no tiene una empresa asignada. Asígnalo a una empresa antes de marcarlo como ocupado.
                </v-alert>
                <v-autocomplete
                  v-else
                  v-model="changeStatusForm.employee"
                  :items="employeeOptions"
                  :loading="loadingEmployees"
                  item-text="name"
                  item-value="id"
                  label="Empleado"
                  prepend-icon="person"
                  outlined
                  dense
                  :search-input.sync="employeeSearch"
                  :no-data-text="'No se encontraron empleados'"
                  :rules="[v => !!v || 'Selecciona un empleado']"
                ></v-autocomplete>
              </v-flex>

              <v-alert
                type="info"
                outlined
                dense
                icon="info"
                class="mb-2"
              >
                <strong>Nota:</strong> Solo administradores del edificio pueden cambiar el estado de los espacios.
              </v-alert>
            </v-layout>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-btn color="grey" @click="dialogChangeStatus = false">
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            dark
            :loading="loadingChangeStatus"
            :disabled="!canConfirmChangeStatus"
            @click="handleChangeStatus"
          >
            <v-icon left small>check_circle</v-icon>
            Cambiar Estado
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import '@/plugins/axios'
import axios from 'axios'
import store from '@/store'

const API_BASE_URL = 'https://empresa-service.intrare.app'

export default {
  name: 'ParkingSpacesStatus',
  data: () => ({
    loading: false,
    isFullscreen: false,
    refreshMinutes: 2,
    refreshIntervalId: null,
    generalError: '',

    selectedCompanyFilter: null,
    selectedTypeFilter: 'all',
    searchSpaces: '',

    dialogSpaceDetail: false,
    selectedSpace: null,

    dialogChangeStatus: false,
    loadingChangeStatus: false,
    changeStatusForm: {
      status: 'disponible',
      block_reason: '',
      employee: null
    },
    statusOptions: [
      { text: 'Disponible', value: 'disponible' },
      { text: 'Ocupado', value: 'ocupado' },
      { text: 'Bloqueado', value: 'bloqueado' },
      { text: 'Mantenimiento', value: 'mantenimiento' }
    ],

    employeeOptions: [],
    loadingEmployees: false,
    employeeSearch: '',
    employeeSearchTimeout: null,

    typeMeta: [
      { type: 'Automóvil', label: 'Automóviles', icon: 'directions_car' },
      { type: 'Motocicleta', label: 'Motocicletas', icon: 'two_wheeler' },
      { type: 'Minusválido', label: 'Minusválidos', icon: 'accessible' }
    ],

    companies: [],
    parkingSpaces: []
  }),

  watch: {
    'changeStatusForm.status'(newStatus) {
      if (newStatus === 'ocupado' && this.selectedSpace && this.selectedSpace.company) {
        this.fetchEmployees('')
      }
    },
    employeeSearch(newVal) {
      if (this.changeStatusForm.status !== 'ocupado' || !this.selectedSpace || !this.selectedSpace.company) return
      if (this.employeeSearchTimeout) clearTimeout(this.employeeSearchTimeout)
      this.employeeSearchTimeout = setTimeout(() => {
        this.fetchEmployees(newVal || '')
      }, 400)
    }
  },

  computed: {
    filteredSpaces() {
      let spaces = [...this.parkingSpaces]

      if (this.selectedCompanyFilter) {
        spaces = spaces.filter(s => s.company === this.selectedCompanyFilter || s.status === 'disponible')
      }

      if (this.searchSpaces) {
        const q = this.searchSpaces.toLowerCase()
        spaces = spaces.filter(s =>
          s.code.toLowerCase().includes(q) ||
          (s.company_name && s.company_name.toLowerCase().includes(q)) ||
          (s.employee_name && s.employee_name.toLowerCase().includes(q))
        )
      }

      return spaces
    },

    filteredGroups() {
      const types = this.selectedTypeFilter === 'all'
        ? this.typeMeta
        : this.typeMeta.filter(t => t.type === this.selectedTypeFilter)

      return types
        .map(meta => ({
          ...meta,
          spaces: this.filteredSpaces.filter(s => s.type === meta.type)
        }))
        .filter(group => group.spaces.length > 0)
    },

    stats() {
      const spaces = this.parkingSpaces
      let available = 0, occupied = 0, blocked = 0, maintenance = 0
      spaces.forEach(sp => {
        if (sp.status === 'ocupado') occupied++
        else if (sp.status === 'bloqueado') blocked++
        else if (sp.status === 'mantenimiento') maintenance++
        else available++
      })
      return {
        total: spaces.length,
        available,
        occupied,
        blocked,
        maintenance
      }
    },

    canConfirmChangeStatus() {
      if (this.loadingChangeStatus || !this.changeStatusForm.status) return false
      if (this.changeStatusForm.status === 'ocupado') {
        return !!(this.selectedSpace && this.selectedSpace.company && this.changeStatusForm.employee)
      }
      return true
    }
  },

  mounted() {
    this.fetchAll()
    this.startAutoRefresh()
    document.addEventListener('fullscreenchange', this.handleFullscreenChange)
  },

  beforeDestroy() {
    if (this.refreshIntervalId) clearInterval(this.refreshIntervalId)
    if (this.employeeSearchTimeout) clearTimeout(this.employeeSearchTimeout)
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    if (this.isFullscreen && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {})
    }
    const appEl = document.getElementById('app')
    if (appEl) appEl.classList.remove('hide-layout-elements-fullscreen')
  },

  methods: {
    authHeaders() {
      return {
        'Content-Type': 'application/json',
        'authorization': 'Token ' + store.getters.getToken
      }
    },

    fetchAll() {
      this.loading = true
      Promise.all([this.fetchCompanies(), this.fetchSpaces()])
        .finally(() => {
          this.loading = false
        })
    },

    fetchCompanies() {
      const config = {
        method: 'get',
        url: `${API_BASE_URL}/parques/empresas`,
        headers: this.authHeaders()
      }
      return axios(config)
        .then(response => {
          this.companies = response.data
        })
        .catch(error => {
          this.generalError = (error.response && error.response.data && error.response.data.error) || 'Error al cargar las empresas'
        })
    },

    fetchSpaces() {
      const config = {
        method: 'get',
        url: `${API_BASE_URL}/Parking/building-spaces/status/`,
        headers: this.authHeaders()
      }
      return axios(config)
        .then(response => {
          this.parkingSpaces = response.data
          if (this.selectedSpace) {
            const updated = this.parkingSpaces.find(s => s.id === this.selectedSpace.id)
            if (updated) this.selectedSpace = updated
          }
        })
        .catch(error => {
          this.generalError = (error.response && error.response.data && error.response.data.error) || 'Error al cargar el estado de los espacios'
        })
    },

    fetchEmployees(search) {
      if (!this.selectedSpace || !this.selectedSpace.company) return
      this.loadingEmployees = true
      const config = {
        method: 'get',
        url: `${API_BASE_URL}/Parking/empleados/`,
        headers: this.authHeaders(),
        params: {
          company: this.selectedSpace.company,
          search: search || undefined
        }
      }
      axios(config)
        .then(response => {
          this.employeeOptions = response.data
        })
        .catch(() => {
          this.employeeOptions = []
        })
        .finally(() => {
          this.loadingEmployees = false
        })
    },

    getSpaceCardClass(space) {
      return {
        'space-available': space.status === 'disponible',
        'space-occupied': space.status === 'ocupado',
        'space-blocked': space.status === 'bloqueado',
        'space-maintenance': space.status === 'mantenimiento'
      }
    },

    getSpaceChipColor(space) {
      if (space.status === 'ocupado') return 'red'
      if (space.status === 'bloqueado') return 'orange'
      if (space.status === 'mantenimiento') return 'grey'
      return 'green'
    },

    getSpaceIconColor(space) {
      if (space.status === 'ocupado') return 'red darken-2'
      if (space.status === 'bloqueado') return 'orange darken-2'
      if (space.status === 'mantenimiento') return 'grey'
      return 'green darken-2'
    },

    getSpaceStatusLabel(space) {
      if (space.status === 'ocupado') return 'Ocupado'
      if (space.status === 'bloqueado') return 'Bloqueado'
      if (space.status === 'mantenimiento') return 'Mantenimiento'
      return 'Disponible'
    },

    openSpaceDetail(space) {
      this.selectedSpace = space
      this.dialogSpaceDetail = true
    },

    openChangeStatusDialog() {
      this.changeStatusForm = {
        status: this.selectedSpace.status,
        block_reason: this.selectedSpace.block_reason || '',
        employee: this.selectedSpace.employee || null
      }
      this.employeeOptions = []
      this.employeeSearch = ''
      this.dialogChangeStatus = true
    },

    handleChangeStatus() {
      if (!this.canConfirmChangeStatus) return

      this.loadingChangeStatus = true

      const data = { status: this.changeStatusForm.status }
      if (this.changeStatusForm.status === 'bloqueado') {
        data.block_reason = this.changeStatusForm.block_reason
      }
      if (this.changeStatusForm.status === 'ocupado') {
        data.employee = this.changeStatusForm.employee
      }

      const config = {
        method: 'patch',
        url: `${API_BASE_URL}/Parking/building-spaces/${this.selectedSpace.id}/`,
        headers: this.authHeaders(),
        data
      }

      axios(config)
        .then(response => {
          const idx = this.parkingSpaces.findIndex(s => s.id === this.selectedSpace.id)
          if (idx !== -1) {
            this.parkingSpaces.splice(idx, 1, response.data)
          }
          this.selectedSpace = response.data
          this.dialogChangeStatus = false
          this.dialogSpaceDetail = false
          this.$store.commit('snackbar_change_message', 'Estado del espacio actualizado')
          this.$store.commit('snackbar_activate_true')
        })
        .catch(error => {
          this.generalError = (error.response && error.response.data && error.response.data.error) ||
            (error.response && error.response.data && error.response.data.employee && error.response.data.employee[0]) ||
            'Error al cambiar el estado del espacio'
        })
        .finally(() => {
          this.loadingChangeStatus = false
        })
    },

    startAutoRefresh() {
      if (this.refreshIntervalId) clearInterval(this.refreshIntervalId)
      const savedMin = localStorage.getItem('parking_status_refresh_minutes')
      if (savedMin) this.refreshMinutes = parseInt(savedMin, 10)
      const minutes = this.refreshMinutes && this.refreshMinutes > 0 ? this.refreshMinutes : 2
      this.refreshIntervalId = setInterval(() => {
        if (!this.dialogSpaceDetail) {
          this.fetchAll()
        }
      }, minutes * 60000)
    },

    updateRefreshInterval() {
      if (this.refreshMinutes && this.refreshMinutes > 0) {
        localStorage.setItem('parking_status_refresh_minutes', this.refreshMinutes)
        this.startAutoRefresh()
      }
    },

    toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {})
      } else {
        if (document.exitFullscreen) document.exitFullscreen()
      }
    },

    handleFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement
      const appEl = document.getElementById('app')
      if (appEl) {
        this.isFullscreen
          ? appEl.classList.add('hide-layout-elements-fullscreen')
          : appEl.classList.remove('hide-layout-elements-fullscreen')
      }
    }
  }
}
</script>

<style scoped>
.space-available {
  border: 2px solid #4CAF50 !important;
  background-color: rgba(76, 175, 80, 0.06) !important;
}
.space-occupied {
  border: 2px solid #F44336 !important;
  background-color: rgba(244, 67, 54, 0.07) !important;
}
.space-blocked {
  border: 2px solid #FF9800 !important;
  background-color: rgba(255, 152, 0, 0.07) !important;
}
.space-maintenance {
  border: 2px solid #9E9E9E !important;
  background-color: rgba(158, 158, 158, 0.07) !important;
}
.parking-space-card:hover {
  transform: scale(1.04);
  box-shadow: 0 6px 20px rgba(0,0,0,0.15) !important;
}
.pointer {
  cursor: pointer;
}
</style>
